import React, { useEffect, useState } from "react";
import {
  StyledBottomNav,
  StyledListButton,
  StyledSlider,
} from "../../Utils/styledComps";
import { Paper, Box, Tooltip, TextField, InputAdornment, Typography } from "@mui/material";
import {
  CenterFocusStrongOutlined,
  Fullscreen,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import {
  IoTabletLandscapeOutline,
  IoTabletPortraitOutline,
} from "react-icons/io5";
import { PiPresentation } from "react-icons/pi";

const Footer = ({
  onZoomChange,
  canvas,
  handlePresentation,
  zoom,
  footerRef,
  toggleOrientation,
  toggleVisibility,
  handleZoomOut,
  handleZoomIn,
  setZoom,
  activeCanvasIndex,
  switchCanvas,
  canvasArray,
  mainCanvas,
  selectedOption,
  handleSelectChange,
}) => {
  function ValueLabelComponent(props) {
    const { children, value } = props;
    return (
      <Tooltip enterTouchDelay={0} arrow placement="bottom" title={value}>
        {children}
      </Tooltip>
    );
  }
  // const [selectedOption, setSelectedOption] = useState(activeCanvasIndex);
  const [orientation, setOrientation] = useState("Portrait");
  const [zoomValue, setZoomValue] = useState(zoom * 100); // Store zoom as string

  useEffect(() => {
    setZoomValue(Math.round(zoom * 100));
  }, [zoom, mainCanvas]);

  const handleZoomInputChange = (e) => {
    let inputValue = e.target.value;

    if (/^-?\d*\.?\d*$/.test(inputValue)) {
      setZoomValue(inputValue);
    }
  };


  const handleZoomEnter = (e) => {
    if (e.key === "Enter") {
      const newZoomValue = Math.round(zoomValue);
      //  parseFloat(zoomValue);
      setZoomValue(newZoomValue);
      onZoomChange(null, newZoomValue);
    }
  };

  const handleOrientation = (orientation) => {
    // Determine the NEW orientation (opposite of current)
    const newOrientation = orientation === "Portrait" ? "Landscape" : "Portrait";
    toggleOrientation(newOrientation);
    setOrientation(newOrientation);
  };
  const roundToDecimals = (value, decimals) => {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  return (
    <Paper
      ref={footerRef}
      sx={{
        position: "fixed",
        zIndex: 1999,
        bottom: 0,
        left: 48,
        right: 0,
        height: "50px",
      }}
      elevation={20}
    >
      <StyledBottomNav
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%",
          px: 2,
        }}
      >
        <Box sx={{ alignItems: "center", display: "flex" }}>
          <TextField
            type="text"
            value={zoomValue}
            onChange={handleZoomInputChange}
            onKeyDown={handleZoomEnter}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip arrow title="Zoom Out">
                    <StyledListButton sx={{ height: "100%", p: 1 }}>
                      <ZoomOut onClick={handleZoomOut} />
                    </StyledListButton>
                  </Tooltip>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <InputAdornment position="end">%</InputAdornment>{" "}
                  {/* Show percentage sign */}
                  <Tooltip arrow title="Zoom In">
                    <StyledListButton sx={{ height: "100%", p: 1 }}>
                      <ZoomIn onClick={handleZoomIn} />
                    </StyledListButton>
                  </Tooltip>
                </InputAdornment>
              ),
              inputProps: {
                style: {
                  textAlign: "center",
                },
              },
              sx: {
                width: "150px",
                padding: 0,
                ml: 2,
                borderRadius: "5px",
                backgroundColor: "transparent",
                "& input[type=number]": {
                  WebkitAppearance: "none", // Hide number spinners
                  MozAppearance: "textfield",
                  appearance: "textfield",
                },
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                "& input[type=number]::-moz-inner-spin-button": {
                  MozAppearance: "none",
                },
              },
            }}
          />
        </Box>

        {/* Other buttons and functionality */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap", // allows wrapping
            maxWidth: "600px", // or whatever width you prefer
            gap: 1, // spacing between buttons
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box
              component="select"
              onChange={handleSelectChange}
              value={selectedOption}
              aria-label="Select page"
              sx={{
                border: 'none',
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                fontWeight: 500,
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                fontSize: '14px',
                cursor: 'pointer',
                transition: (theme) => theme.transitions.create(['box-shadow', 'transform'], {
                  duration: theme.transitions.duration.shorter,
                }),
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  transform: 'translateY(-1px)',
                },
                '&:focus-visible': {
                  outline: (theme) => `2px solid ${theme.palette.action.focus}`,
                  outlineOffset: 2,
                },
              }}
            >
              {canvasArray.map((canvas, index) => (
                <option key={index} value={index}>
                  {`Page ${index + 1}`}
                </option>
              ))}
            </Box>

            <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
              {selectedOption + 1} / {canvasArray.length}
            </Typography>
          </Box>

          <Tooltip arrow title="Center visible">
            <StyledListButton
              sx={{ maxWidth: 60, px: 5.5 }}
              onClick={toggleVisibility}
              aria-label="Center visible"
            >
              <CenterFocusStrongOutlined />
              <Typography sx={{ fontSize: 10, ml: 0.75 }}>Center Visible</Typography>
            </StyledListButton>
          </Tooltip>

          <Tooltip arrow placement="top" title="Presentation Mode">
            <StyledListButton
              sx={{ maxWidth: 100, px: 0 }}
              onClick={handlePresentation}
              aria-label="Presentation mode"
            >
              <PiPresentation size={24} />
              <Typography sx={{ fontSize: 10, ml: 0.75 }}>Presentation Mode</Typography>
            </StyledListButton>
          </Tooltip>

          <Tooltip
            arrow
            placement="top"
            title={orientation === "Portrait" ? "Landscape Mode" : "Portrait Mode"}
          >
            <StyledListButton
              sx={{ maxWidth: 100, px: 0 }}
              onClick={() => handleOrientation(orientation)}
              aria-label={`Switch to ${orientation === "Portrait" ? "Landscape" : "Portrait"} mode`}
            >
              {orientation === "Portrait" ? (
                <IoTabletPortraitOutline size={20} />
              ) : (
                <IoTabletLandscapeOutline size={20} />
              )}
              <Typography sx={{ fontSize: 10, ml: 0.75 }}>
                {orientation === "Portrait" ? "Landscape" : "Portrait"}
              </Typography>
            </StyledListButton>
          </Tooltip>

          <Tooltip
            arrow
            placement="top"
            title={`Canvas Dimensions: Height:${Math.floor(
              canvas?.getHeight() / canvas?.getZoom()
            )} || Width:${Math.floor(
              canvas?.getWidth() / canvas?.getZoom()
            )} || Zoom:${canvas?.getZoom()}`}
          >
            <Box sx={{ display: 'flex', gap: 1.25, mr: 2.5, pl: 2 }}>
              <Box
                sx={{
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignContent: 'center',
                }}
              >
                <Typography sx={{ p: 0, m: 0, fontSize: 10, color: 'text.primary' }}>
                  H-{Math.floor(canvas?.getHeight() / canvas?.getZoom())}
                </Typography>
                <Typography sx={{ p: 0, m: 0, fontSize: 10, color: 'text.primary' }}>
                  W-{Math.floor(canvas?.getWidth() / canvas?.getZoom())}
                </Typography>
                <Typography sx={{ p: 0, m: 0, fontSize: 10, color: 'text.primary' }}>
                  Z-{roundToDecimals(canvas?.getZoom(), 2)}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        </Box>
      </StyledBottomNav>
    </Paper>
  );
};

export default Footer;
